import { Grid, styled } from '@mui/material';
import dayjs from 'dayjs';
import { cloneDeep, sumBy } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    convertDateFormat,
    getFormattedDate,
    getGoalPriorityProbability,
    buildWealthPathChartData
} from '../../common/helper';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { Typography } from '../../components/atoms/Typography/Typography';
import {
    ConfirmationCentered,
    PDFFooter,
    PDFHeader,
    PlanInfoAndReRunSection,
    UniversalCard
} from '../../components/molecules';
import { PDFAllocationsOverTimeChart } from '../../components/molecules/AllocationsOverTimeChart/PDFAllocationsOverTimeChart';
import { GoalType } from '../../components/molecules/GoalType/GoalType';
import {
    PlanSummaryProgressBar,
    PlanSummaryProgressBarValue
} from '../../components/molecules/PlanSummaryProgressBar/PlanSummaryProgressBar';
import { ProbabilityOfSuccess } from '../../components/molecules/ProbabilityOfSuccess/ProbabilityOfSuccess';
import { ProjectedWealthPathChart } from '../../components/molecules/ProjectedWealthPathChart';
import { PDFProjectedWealthPathChart } from '../../components/molecules/ProjectedWealthPathChart/PDFProjectedWealthPathChart';
import { Tabs, TabVariant } from '../../components/molecules/Tabs/Tabs';
import { GoalPriorityChip } from '../../components/molecules/GoalPriorityChip/GoalPriorityChip';
import PortfolioCompositionContainer from '../../components/organisms/PortfolioComposition';
import { PDFPortfolioComposition } from '../../components/organisms/PortfolioComposition/PDFPortfolioComposition';
import useMarketPortfolioData from '../../components/organisms/PortfolioComposition/useMarketPortfolioData';
import { RetirementGoalsFF } from '../../containers/retirement-goals-drawer/RetirementGoalsDrawerFF';
import { WealthGoals } from '../../containers/wealth-goals-drawer';
import { updateCurrentStep } from '../../features/client';
import { saveGoal, syncAdviceGoals } from '../../features/client-goals';
import { goalcalculator } from '../../services/clients/goalcalculator';
import { runpipe } from '../../services/clients/runpipe';
import { calculateYearsBetweenDates, getGOEHeaders } from '../../utils/index';
import { AllocationsOverTimeTab } from './AllocationsOverTimeTab';
import { setOpenDrawer, updateGlobalLoaderState } from '../../features/global/globalSlice';
import { useCMSContent } from '../../common/hooks/useCmsContent';

export interface RecomendationProps {
    isRecomendation?: boolean;
}

export const StyledDisclaimerContent = styled(Typography)(({ theme }) => ({
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    margin: `${theme.spacing(3)} 0`,
    lineHeight: theme.spacing(2),
    color: theme.palette.neutral[400]
}));

const ProbSuccessDiv = styled('div')<RecomendationProps>(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    gap: '32px'
}));

const HeaderDiv = styled('div')(({ isPDF }: { isPDF: boolean }) => ({
    display: 'flex',
    alignItems: 'start',
    marginBottom: '20px',
    paddingBottom: isPDF ? '10px' : 'inherit',
    borderBottom: isPDF ? '1px solid #BFBFBF' : 'inherit',
    marginTop: isPDF ? '22px' : '32px'
}));

const StyledChipContainer = styled('div')(() => ({
    marginBottom: '3px',
    marginLeft: '12px',
    marginTop: '0.30rem',
    '& > div': {
        justifyContent: 'center'
    }
}));

const GoalSummary = (props: any) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        goalId,
        goalJson,
        goeConfig,
        goalsData,
        goalcalculatorJson,
        goalName,
        goalType,
        isGoalRealistic,
        isRetirement,
        goalDataRecomendation,
        updateStep,
        type,
        isPDF,
        callRunpipe,
        goalsWithNoFundingSource
    } = props;
    const global = useAppSelector((state) => state.global);
    const cmsUtil = useCMSContent();
    const { portfolio: allMarketPortfolios } = useAppSelector((state) => state.global);
    const cms_projectedAllocationPart1_asElement = cmsUtil.getContentAsElement(
        'plan_detail.projected_allocation.part1'
    );
    const cms_projectedAllocationPart1_asString = cmsUtil.getContentAsString('plan_detail.projected_allocation.part1');
    const cms_portfolioCompositionDescription = cmsUtil.getContentAsString('plan_detail.footer.part1');
    const cms_RiskToleranceDescription = cmsUtil.getContentAsString('risk_tolerance.content.disclaimer1');
    const selectedGoal = useAppSelector(
        (state) => state?.adviceGoals?.formFields?.find((goal: any) => goal.goalId === goalId)?.data
    );

    const { portfolioCompositionSource } = useMarketPortfolioData(selectedGoal?.goalCategory);
    const marketPortfoliosForComposition = portfolioCompositionSource?.length
        ? portfolioCompositionSource
        : allMarketPortfolios;
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    // In case particular runpipe or goalCalculator fails we don't proceed generating that Plan tab
    if (goalJson?.length === 0 || goalcalculatorJson?.length === 0) return <></>;
    const goalResp = cloneDeep(goalJson[0]);
    const infusionIndex = useRef(0);
    // 4 pages in PDF per goal + 1 page per unrealistic goal + 1 for last page
    const totalPagesForPDF =
        adviceGoals?.response.reduce(
            (acc, goal) => (goal?.response?.body?.analysisReport?.isGoalRealistic ? acc + 4 : acc + 1),
            0
        ) + 1;
    const isRecommendationApplied =
        (goalDataRecomendation?.oneTimeTopUp ||
            goalDataRecomendation?.recomendedTenure ||
            goalDataRecomendation?.topUpDecumulation ||
            goalDataRecomendation?.topUpAccumulation) &&
        goalDataRecomendation?.originalData;
    // will use this later if required
    // const findYears = useCallback(
    //     (probData: any) => {
    //         if (probData != 'NA') {
    //             if (!isRetirement) {
    //                 const splitedData = probData.split(';');
    //                 let years = 0;
    //                 let splitedDataPer = splitedData[0].split(':');
    //                 const splitedDataPerT = splitedDataPer[0].trim().split(' ');
    //                 years = parseInt(splitedDataPerT[3].split('+')[1]);
    //                 return years;
    //             } else {
    //                 const splitedData = probData.split(';');
    //                 let years = 0;
    //                 let splitedDataPer = splitedData[0].split(':');
    //                 if (splitedData.length > 1) {
    //                     splitedDataPer = splitedData[1].split(':');
    //                 }
    //                 const splitedDataPerT = splitedDataPer[0].trim().split(' ');
    //                 splitedDataPerT.forEach((element: any) => {
    //                     if (Number(element)) {
    //                         years = Number(element);
    //                     }
    //                 });
    //                 return years;
    //             }
    //         }
    //         return 0;
    //     },
    //     [goalResp]
    // );

    function calculateAmountAfterMonths(startDateInput: string, endDateInput: string): number {
        // Helper function to parse DD-MM-YYYY formatted date
        const parseDate = (dateInput: string): Date => {
            const [month, day, year] = dateInput.split('-').map(Number);
            return new Date(year, month - 1, day); // months are 0-indexed in JavaScript Date
        };

        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // current month is 0-indexed, so add 1

        const startDate = parseDate(startDateInput);
        const endDate = parseDate(endDateInput);

        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth() + 1;
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth() + 1;

        //out of range: future or past years
        if (currentYear < startYear || currentYear > endYear) {
            return 0;
        }
        //current year, start year and end year match
        if (currentYear === startYear && currentYear === endYear) {
            return endMonth - startMonth + 1;
        }

        if (currentYear === startYear) {
            return 12 - (startMonth - 1); // Current year and start year match
        }
        if (currentYear === endYear) {
            if (currentMonth <= endMonth) {
                return endMonth; // Current year and end year match, and current month <= end month
            } else {
                return 0; // Current month is after the end month
            }
        }
        return 12; // Default case when current year is between start year and end year
    }

    const calculateInitialAmount = (accounts = []) => {
        const { incomeSource = [], investmentSource } =
            adviceGoals?.formFields?.find?.((item) => item.goalId === goalId)?.data?.sources || {};
        const sumIncomeSource = sumBy(
            incomeSource,
            ({ isSelected, accountBalance, beginningYear = '01-01-2025', endYear = '01-01-2025', frequency }: any) => {
                if (!isSelected) {
                    return 0;
                }
                let partsbeginningYear = beginningYear.split('-');
                let partsendYear = endYear.split('-');
                // Rearrange the parts to mm-dd-yyyy format
                let formattedDatebeginningYear = `${partsbeginningYear[1]}-${partsbeginningYear[0]}-${partsbeginningYear[2]}`;
                let formattedendYear = `${partsendYear[1]}-${partsendYear[0]}-${partsendYear[2]}`;
                const result = calculateAmountAfterMonths(formattedDatebeginningYear, formattedendYear);
                const updatedAmount =
                    frequency === 'Annually' ? result * accountBalance : result * (accountBalance / 12);
                return updatedAmount;
            }
        );

        const sumInvestmentSource = sumBy(
            investmentSource,
            ({
                isRCSelected,
                recurringContributions,
                recurringContributionsStartDate,
                recurringContributionsEndDate
            }: any) => {
                if (!isRCSelected) {
                    return 0;
                }
                const result = calculateAmountAfterMonths(
                    recurringContributionsStartDate || '01-01-2025',
                    recurringContributionsEndDate || '01-01-2025'
                );
                const updatedAmount = result * recurringContributions;
                return updatedAmount;
            }
        );

        const annualContribution = sumIncomeSource + sumInvestmentSource;

        const initialInvestment = sumBy(accounts, 'amount') || 0;
        return { initialInvestment, annualContribution };
    };

    const callRunpipeNGoalcalculator = async (runpipePayload?: any, goalPayload?: any) => {
        let promiseArry = [];
        let op: any = [];
        promiseArry.push(runpipe(runpipePayload, getGOEHeaders(global?.orgInfo?.orgEmail)));
        promiseArry.push(goalcalculator(goalPayload, getGOEHeaders(global?.orgInfo?.orgEmail)));
        op = await Promise.all(promiseArry);
        return op;
    };
    // recommendationType will be blank for reset, goalData will have updated data with recommendations, value is recommended value related to respective recommendation
    const updatePlanDetails = async (recommendationType: string, value: number, runpipeRequest: any) => {
        dispatch(updateGlobalLoaderState(true));
        const goalCalculatorRequest = { ...runpipeRequest, goalAmount: 0 };
        // removing realloc_schedules from goalCalculatorRequest ref: AEX-1831
        delete goalCalculatorRequest['realloc_schedules'];
        // updating realloc_schedules in runpipeRequest ref: AEX-2019
        runpipeRequest['realloc_schedules'] = global?.goeConfig?.reallocSchedules;
        const [runpipeResponse, GcalculatorResponse] = await callRunpipeNGoalcalculator(
            runpipeRequest,
            goalCalculatorRequest
        );
        const runpipeRequestData = adviceGoals.request.map((item: any) => {
            return item?.goalId && item.goalId === goalId ? runpipeRequest : item;
        });
        const runpipeResponseData = adviceGoals.response.map((item: any) => {
            return item?.goalId && item.goalId === goalId ? { ...item, ...runpipeResponse } : item;
        });
        const recomendationResp = adviceGoals?.recommendation
            ? adviceGoals?.recommendation?.map((item: any) => {
                  return item?.goalId && item.goalId === goalId
                      ? {
                            ...item,
                            [recommendationType]: value,
                            // saving original goal data and calculator response to utilise it while resetting and avoiding API call.
                            originalData: {
                                formFields: adviceGoals?.formFields?.find((goal) => goal?.goalId === goalId)?.data,
                                request: adviceGoals?.request?.find((goal) => goal?.goalId === goalId),
                                response: adviceGoals?.response?.find((goal) => goal?.goalId === goalId),
                                responseGoalCalculator: adviceGoals?.global?.responseGoalCalculator?.find(
                                    (goal) => goal?.goalId === goalId
                                )
                            }
                        }
                      : item;
              })
            : adviceGoals?.formFields?.map((item: any) => {
                  return item?.goalId && item.goalId === goalId
                      ? {
                            oneTimeTopUp: 0,
                            recomendedTenure: 0,
                            topUpAccumulation: 0,
                            topUpDecumulation: 0,
                            goalId: item.goalId,
                            [recommendationType]: value,
                            // saving original goal data and calculator response to utilise it while resetting and avoiding API call.
                            originalData: {
                                formFields: adviceGoals?.formFields?.find((goal) => goal?.goalId === goalId)?.data,
                                request: adviceGoals?.request?.find((goal) => goal?.goalId === goalId),
                                response: adviceGoals?.response?.find((goal) => goal?.goalId === goalId),
                                responseGoalCalculator: adviceGoals?.global?.responseGoalCalculator?.find(
                                    (goal) => goal?.goalId === goalId
                                )
                            }
                        }
                      : {
                            goalId: item.goalId,
                            oneTimeTopUp: 0,
                            topUpAccumulation: 0,
                            topUpDecumulation: 0,
                            recomendedTenure: 0
                        };
              });
        const GcalculatorResponseData = adviceGoals?.global?.responseGoalCalculator?.map((item: any) => {
            return item?.goalId && item.goalId === goalId ? { ...item, ...GcalculatorResponse } : item;
        });

        dispatch(
            await saveGoal({
                ...adviceGoals,
                request: runpipeRequestData,
                response: runpipeResponseData,
                recommendation: recomendationResp,
                infusionindex: { [goalId]: infusionIndex.current },
                global: {
                    ...adviceGoals?.global,
                    enablePlanDetails: false,
                    responseGoalCalculator: GcalculatorResponseData
                }
            })
        );
        dispatch(updateGlobalLoaderState(false));
    };

    const applyRecommendation = async (selectedRecomendationData: any) => {
        const goalJson = cloneDeep(goalsData);
        if (selectedRecomendationData.key === 'oneTimeTopUp') {
            goalJson.accounts.push({
                fundingType: 'investmentAccounts',
                recommendationType: 'oneTimeTopUp',
                amount: selectedRecomendationData.optionValue,
                recurringContribution: 0,
                escalationPercentage: 0,
                escalationYears: 1,
                frequency: 'yearly',
                startDate: dayjs().format('DD-MM-YYYY'),
                endDate: goalsData.targetDate
            });
            try {
                await updatePlanDetails('oneTimeTopUp', selectedRecomendationData.optionValue, goalJson);
            } catch (error) {
                console.error(error);
            }
        } else if (selectedRecomendationData.key === 'topUpAccumulation') {
            goalJson.accounts.push({
                fundingType: 'investmentAccounts',
                amount: 0,
                recommendationType: 'topUpAccumulation',
                recurringContribution: selectedRecomendationData.optionValue,
                escalationPercentage: 0,
                escalationYears: 1,
                frequency: 'yearly',
                startDate: dayjs().format('DD-MM-YYYY'),
                endDate: isRetirement
                    ? dayjs(goalsData.planStartRetirement, 'DD-MM-YYYY').format('DD-MM-YYYY')
                    : goalsData.targetDate,
                includeRecurringContributionsForGoal: goalId
            });
            try {
                await updatePlanDetails('topUpAccumulation', selectedRecomendationData.optionValue, goalJson);
            } catch (error: any) {
                console.error(error);
            }
        } else if (selectedRecomendationData.key === 'recommendedTenure') {
            const dateStr = goalJson.targetDate;
            const [day1, month1, year1] = dateStr.split('-').map(Number);
            const date = new Date(year1, month1 - 1, day1);
            const newDate = date.setFullYear(date.getFullYear() + selectedRecomendationData.optionValue);
            var updatedDate = new Date(newDate);
            // Extract day, month, and year
            var day = updatedDate.getDate();
            var month = updatedDate.getMonth() + 1;
            var year = updatedDate.getFullYear();
            const formattedDay: string = day < 10 ? '0' + day : day.toString();
            const formattedMonth: string = month < 10 ? '0' + month : month.toString();
            // Format the date as dd-mm-yyyy
            const formattedDate: string = formattedDay + '-' + formattedMonth + '-' + year;
            if (!isRetirement) goalJson['targetDate'] = formattedDate;
            if (isRetirement) {
                const dateStr = goalsData.planStartRetirement;
                const [day1, month1, year1] = dateStr.split('-').map(Number);
                const date = new Date(year1, month1 - 1, day1);
                const newDate = date.setFullYear(date.getFullYear() + selectedRecomendationData.optionValue);
                updatedDate = new Date(newDate);
                // Extract day, month, and year
                day = updatedDate.getDate();
                month = updatedDate.getMonth() + 1;
                year = updatedDate.getFullYear();
                const formattedDay: string = day < 10 ? '0' + day : day.toString();
                const formattedMonth: string = month < 10 ? '0' + month : month.toString();
                // Format the date as dd-mm-yyyy
                const formattedDate: string = formattedDay + '-' + formattedMonth + '-' + year;
                goalJson['planStartRetirement'] = formattedDate;
            }
            try {
                await updatePlanDetails('recomendedTenure', selectedRecomendationData.optionValue, goalJson);
            } catch (error) {
                console.error(error);
            }
        } else if (isRetirement && selectedRecomendationData.key === 'topUpDecumulation') {
            const newIncome = goalJson['targetedRetirementIncome'] - selectedRecomendationData.optionValue;
            goalJson['targetedRetirementIncome'] = newIncome;
            try {
                await updatePlanDetails('topUpDecumulation', selectedRecomendationData.optionValue, goalJson);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const resetRecommendation = () => {
        if (isRecommendationApplied && adviceGoals?.recommendation) {
            const recomendationResp = adviceGoals.recommendation.map((item: any) => {
                return item?.goalId && item.goalId === goalId
                    ? {
                          goalId,
                          oneTimeTopUp: 0,
                          topUpAccumulation: 0,
                          topUpDecumulation: 0,
                          recomendedTenure: 0
                      }
                    : item;
            });
            dispatch(
                saveGoal({
                    ...adviceGoals,
                    formFields: adviceGoals?.formFields?.map((item) =>
                        item?.goalId && item.goalId === goalId
                            ? { goalId, data: goalDataRecomendation?.originalData?.formFields }
                            : item
                    ),
                    request: adviceGoals?.request?.map((item) =>
                        item?.goalId && item.goalId === goalId ? goalDataRecomendation?.originalData?.request : item
                    ),
                    response: adviceGoals?.response?.map((item) =>
                        item?.goalId && item.goalId === goalId ? goalDataRecomendation?.originalData?.response : item
                    ),
                    recommendation: recomendationResp,
                    global: {
                        ...adviceGoals?.global,
                        enablePlanDetails: false,
                        responseGoalCalculator: adviceGoals?.global?.responseGoalCalculator?.map((item) =>
                            item?.goalId && item.goalId === goalId
                                ? goalDataRecomendation?.originalData?.responseGoalCalculator
                                : item
                        )
                    }
                })
            );
        }
    };

    const goalAmtRetirement = (arr: any, accounts: any) => {
        let min = 0;
        for (let i of arr) {
            if (i < 0) {
                min = i;
                break;
            }
        }
        // We ignore Accounts and probableGoalAmt is 0, ref: AEX-1793
        let retirementIncome = 0;
        accounts?.forEach((acc: any) => {
            if (acc?.fundingType === 'incomeSources') {
                if (acc?.frequency === 'monthly') retirementIncome += 12 * acc?.recurringContribution;
                else retirementIncome += acc?.recurringContribution; //its amount from income sources
            }
        });
        return -1 * min + retirementIncome;
    };

    const originalGoalAmtRetirement = (originalData: any) => {
        const goalCalculatorData = originalData?.responseGoalCalculator;
        const infusionArr = goalCalculatorData?.['response']?.['body']?.['infusions'] || [];
        const accounts = originalData?.request?.accounts || [];
        return goalAmtRetirement(infusionArr, accounts);
    };

    const infusionArray = goalcalculatorJson?.[0]?.['response']?.['body']?.['infusions'] || [];
    const [tabIndex, setTabIndex] = useState('0');
    const calculatedAmountval = calculateInitialAmount(goalsData.accounts);
    const analysisReport = goalResp?.['response']?.['body']?.['analysisReport'];
    const pathReport = goalResp?.response?.body?.pathReport;
    const goePayload = goalResp?.['goePayload'];
    const scenarioType = goePayload?.['scenarioType'];
    const goalPriorityValue = goeConfig?.goalPriorityProbabilities;
    const goalUnrealisticValue = goeConfig?.realisticGoalProb;
    const currentGoalProbability = Math.round(Number(analysisReport?.['currentGoalProbability'] * 100));
    const goalPriorityProbability = getGoalPriorityProbability(goalPriorityValue, goalsData?.['goalPriority']) * 100;
    const isRecomendation = analysisReport?.['meetGoalPriority'];
    const probableGoalAmount = isRetirement
        ? goalAmtRetirement(infusionArray, goalsData.accounts)
        : goalcalculatorJson[0]?.response?.body?.goalAmt;
    const goalAmount = isRetirement ? goalsData['targetedRetirementIncome'] : goalsData['goalAmount'];
    const bequestAmount = goalsData['goalAmount'];
    // const originalAmount = goalDataRecomendation[0].orginalAmount;
    // const isRecomendationClicked =
    //     goalDataRecomendation[0].oneTimeTopUp != 0 ||
    //     goalDataRecomendation[0].topUpAccumulation != 0 ||
    //     goalDataRecomendation[0].toUpDeccumulation != 0 ||
    //     goalDataRecomendation[0].recomendedTenure != 0;

    // const isRecomendation = isRetirement ? (analysisReport['oneTimeTopUp'] == 0 && analysisReport['yearlyTopUpAccumulation']==0 && recomendedTenure==0 && analysisReport['yearlyTopUpDecumulation']<=0) : (analysisReport['oneTimeTopUp'] == 0 &&
    // analysisReport['yearlyTopUpAccumulation']==0 && recomendedTenure==0); // To Do on decumulation negative value // for future use
    const wealthPath = adviceGoals.response.find((item: any) => {
        return item?.goalId && item.goalId === goalId;
    })?.response?.body?.pathReport?.wealthPath;

    function extractYearRetirement(data: string, value: number): number | null {
        // Split the data string into individual parts
        const parts = data.split(';').map((part) => part.trim());

        const adjustments: { [key: number]: number } = {};

        // Process each part to extract years and percentages
        parts.forEach((part) => {
            const years = parseInt(part.split('by')[1].split('years')[0].trim());
            const percentage = parseInt(part.split(':')[1].replace('%', '').trim());
            adjustments[years] = percentage;
        });

        // Iterate through the adjustments to find the first percentage greater than the value
        for (const years of Object.keys(adjustments).map(Number)) {
            if (adjustments[years] >= value) {
                return years; // Return the first years found with percentage greater than value
            }
        }

        return 0; // Return null if no suitable adjustment is found
    }

    // will use this later
    function extractYearForProbability(probData: string): number | null {
        if (probData === 'NA') {
            return 0; // or any other indication of invalid input
        }

        let resultYear: number | null = 0;
        if (isRetirement) {
            resultYear = extractYearRetirement(probData, goalPriorityProbability);
        } else {
            // Loop through the split data
            const splitedData = probData.split(';');
            for (const dataEntry of splitedData) {
                const match = dataEntry.match(/T\+(\d+) years\s*:\s*(\d+)%/);
                if (match) {
                    const year = parseInt(match[1]);
                    const percentage = parseInt(match[2]);
                    // Check if the percentage is greater than or equal to the target value
                    if (percentage >= goalPriorityProbability) {
                        return year; // Return the first year that meets the condition
                    }
                }
            }
        }
        return resultYear; // Return the year or 0 if not found
    }

    const chartData = buildWealthPathChartData(wealthPath, goalResp?.charts?.wealthPath?.xAxis);
    const [deleteModalState, setDeleteModalState] = useState(false);
    const [showGoalDrawer, setShowGoalDrawer] = useState(false);
    const [resetModalState, setResetModalState] = useState(false);

    const yearsInRetirement = calculateYearsBetweenDates(
        isRetirement && goalsData?.planStartRetirement ? goalsData?.planStartRetirement : '',
        isRetirement && goalsData?.targetDate ? goalsData?.targetDate : ''
    );
    const handleCancelDelete = () => {
        setDeleteModalState(false);
    };

    const handleConfirmDelete = () => {
        const updatedGoals = adviceGoals.formFields.filter((goal) => goal.goalId !== goalId);
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                formFields: updatedGoals
            })
        );
        dispatch(updateCurrentStep(1));
        updateStep(1);
    };

    const handleConfirmReset = () => {
        setResetModalState(true);
    };
    // TODO:: Keeping this comment until we find a space to put in 👇

    // useEffect(() => {
    //     if (adviceGoals.global.probabilityOfSuccess !== currentGoalProbability) {
    //         dispatch(
    //             syncAdviceGoals({
    //                 ...adviceGoals,
    //                 global: { ...adviceGoals?.global, probabilityOfSuccess: currentGoalProbability }
    //             })
    //         );
    //     }
    // }, [currentGoalProbability, adviceGoals, dispatch]);

    const barPrecentage =
        probableGoalAmount < goalAmount
            ? (probableGoalAmount / goalAmount) * 100
            : (goalAmount / probableGoalAmount) * 100;

    const planSummaryProgressBarValues = useMemo<PlanSummaryProgressBarValue[]>(() => {
        let targetGoalAmountLabel = '';

        if (probableGoalAmount >= goalAmount) {
            targetGoalAmountLabel = isRetirement ? t('TARGET_ANNUAL_INCOME') : t('TARGET_GOAL');
        } else {
            targetGoalAmountLabel = isRetirement ? t('PROJECTED_ANNUAL_INCOME') : t('PROJECTED_GOAL');
        }

        if (goalAmount == probableGoalAmount) {
            return [
                {
                    color: '#416AF4',
                    label: targetGoalAmountLabel,
                    percentage: 100
                }
            ];
        }

        if (probableGoalAmount < goalAmount) {
            return [
                {
                    color: '#416AF4',
                    label: targetGoalAmountLabel,
                    percentage: barPrecentage
                },
                {
                    color: '#E4E5E8',
                    label: t('SHORTFALL_BELOW_TARGET'),
                    descText: t('SHORTFALL_BELOW_TARGET'),
                    percentage: 100 - barPrecentage
                }
            ];
        }

        return [
            {
                color: '#416AF4',
                label: targetGoalAmountLabel,
                percentage: barPrecentage
            },
            {
                percentage: 100 - barPrecentage,
                color: '#00BFB3',
                label: t('EXCESS_OVER_TARGET'),
                descText: t('EXCESS_OVER_TARGET')
            }
        ];
    }, [goalAmount, probableGoalAmount, barPrecentage, isRetirement]);

    const calculateAnnualContribution = (goePayload: any) => {
        let anualContributionDict: any;
        let infusionsArray: { infusion: any; realisationDt: dayjs.Dayjs }[] = [];
        let cashflowDt = dayjs(goePayload?.cashflowDate, 'DD-MM-YYYY');
        let startDt = dayjs(goePayload?.currDate, 'DD-MM-YYYY');
        let endDt = dayjs(goePayload?.endDate, 'DD-MM-YYYY');
        let yrdiff = endDt.diff(startDt, 'year') + 1;
        goePayload?.infusions &&
            goePayload?.infusions.forEach((infusion: any, index: number) => {
                if (index == goePayload?.infusions?.length - 1) {
                    infusionsArray.push({
                        infusion: infusion,
                        realisationDt: endDt
                    });
                    return;
                }
                if (yrdiff < goePayload?.infusions.length && cashflowDt != startDt) {
                    if (index == 0) {
                        infusionsArray.push({
                            infusion: infusion,
                            realisationDt: startDt
                        });
                        return;
                    }
                    index = index - 1;
                }
                infusionsArray.push({
                    infusion: infusion,
                    realisationDt: cashflowDt.add(index, 'year')
                });
            });
        anualContributionDict = {
            infusion: 0,
            realisationDt: startDt.format('DD-MM-YYYY'),
            isFinalYear: infusionsArray.length <= 2
        };
        if (isRecommendationApplied && goalId in adviceGoals.infusionindex && adviceGoals.infusionindex[goalId] !== 0) {
            let lastinfusionindex = adviceGoals.infusionindex[goalId] ?? 0;
            anualContributionDict.infusion = infusionsArray[lastinfusionindex].infusion;
            anualContributionDict.realisationDt = infusionsArray[lastinfusionindex].realisationDt.format('DD-MM-YYYY');
        } else {
            let i = 0;
            for (const element of infusionsArray as any) {
                if (element.infusion > 0) {
                    anualContributionDict.infusion = element.infusion;
                    anualContributionDict.realisationDt = element.realisationDt.format('DD-MM-YYYY');
                    anualContributionDict.isFinalYear = false;
                    infusionIndex.current = i;
                    break;
                }
                i++;
            }
        }
        return anualContributionDict;
    };

    const anualContributionDict = useMemo(() => {
        return calculateAnnualContribution(goePayload);
    }, [
        goePayload,
        calculatedAmountval.annualContribution,
        isRecommendationApplied,
        goalId,
        adviceGoals.infusionindex
    ]);
    //AEX-1899 end

    const originalAnnualContributionDict = useMemo(() => {
        if (goalDataRecomendation?.originalData?.response?.goePayload) {
            return calculateAnnualContribution(goalDataRecomendation?.originalData?.response?.goePayload);
        }
        return null;
    }, [goalDataRecomendation?.originalData?.response?.goePayload]);

    const handleTabChange = (event: React.SyntheticEvent, value: string) => {
        setTabIndex(value);
        return value;
    };

    const formField = adviceGoals.formFields.find((goal) => goal.goalId === goalId);
    const title = formField?.data?.wealthGoalType;

    const { goalPriority } = goalsData || {};

    const GoalHeaderContent = () => (
        <>
            <HeaderDiv isPDF={isPDF}>
                <GoalType type={type} isPDF={isPDF} />
                <Typography variant="headers.h2" label={`${goalName} |`} aria-label={goalName} ml={1.5} />
                <Typography
                    label={t(goalType?.toUpperCase())}
                    aria-label={goalType}
                    ml={0.4}
                    mr={1.5}
                    variant="headers.h2Medium"
                />
                <StyledChipContainer>
                    {goalPriority && <GoalPriorityChip goalPriority={goalPriority} />} &nbsp;
                </StyledChipContainer>
            </HeaderDiv>
        </>
    );
    const GoalContent = () => (
        <>
            <GoalHeaderContent />
            <PlanSummaryProgressBar
                highlightedValues={
                    isRetirement
                        ? [
                              {
                                  amount: goalAmount,
                                  label: t('TARGET_ANNUAL_INCOME'),
                                  helperText: t('TARGET_ANNUAL_INCOME_HELPER'),
                                  originalAmount:
                                      isRecommendationApplied &&
                                      goalAmount !==
                                          goalDataRecomendation?.originalData?.request?.targetedRetirementIncome
                                          ? goalDataRecomendation?.originalData?.request?.targetedRetirementIncome
                                          : 0
                              },
                              {
                                  amount: probableGoalAmount,
                                  label: t('PROJECTED_ANNUAL_INCOME'),
                                  helperText: t('PROJECTED_ANNUAL_INCOME_HELPER'),
                                  helperTextProps: {
                                      thresholdValue: goalPriorityProbability
                                  },
                                  originalAmount: isRecommendationApplied
                                      ? originalGoalAmtRetirement(goalDataRecomendation?.originalData)
                                      : 0
                              },
                              ...(bequestAmount
                                  ? [
                                        {
                                            amount: bequestAmount,
                                            label: t('GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT'),
                                            helperText: t('BEQUEST_AMOUNT_HELPER_TEXT')
                                        }
                                    ]
                                  : [])
                          ]
                        : [
                              {
                                  amount: goalAmount,
                                  label: t('TARGET_GOAL'),
                                  helperText: t('TARGET_GOAL_HELPER') + convertDateFormat(goalsData?.targetDate)
                              },

                              {
                                  amount: probableGoalAmount,
                                  label: t('PROJECTED_GOAL'),
                                  helperText: t('PROJECTED_GOAL_HELPER'),
                                  helperTextProps: {
                                      thresholdValue: goalPriorityProbability
                                  },
                                  originalAmount: isRecommendationApplied
                                      ? goalDataRecomendation?.originalData?.responseGoalCalculator?.response?.body
                                            ?.goalAmt
                                      : 0
                              }
                          ]
                }
                onClickEditGoals={() => setShowGoalDrawer(true)}
                targetProgress={goalAmount < probableGoalAmount ? barPrecentage : 100}
                values={planSummaryProgressBarValues}
                isPDF={isPDF}
                showReset={isRecommendationApplied}
                onClickReset={handleConfirmReset}
            />
            <ConfirmationCentered
                cancelButtonLabel={`${t('BUTTON_CANCEL')}`}
                confirmButtonLabel={`${t('CONFIRM_BUTTON')}`}
                title={`${t('RESET_TITLE')}`}
                content={`${t('RESET_MODAL_CONTENT')}`}
                onCancellation={() => setResetModalState(false)}
                onConfirmation={() => resetRecommendation()}
                open={resetModalState}
                onCloseIconClick={() => setResetModalState(false)}
            />
            <ProbSuccessDiv>
                <ProbabilityOfSuccess
                    dangerValue={goalUnrealisticValue * 100}
                    goalType={goalsData['goalPriority']}
                    target={goalPriorityProbability}
                    thresholdValue={goalPriorityProbability}
                    value={currentGoalProbability}
                    isPDF={isPDF}
                />
                <PlanInfoAndReRunSection
                    anualContributionDict={anualContributionDict}
                    originalAnnualContributionsAmount={originalAnnualContributionDict?.infusion ?? null}
                    handleReRunButtonClicked={(selectedRecomendation) => {
                        applyRecommendation(selectedRecomendation);
                    }}
                    initialInvestmentAmount={
                        goalsData?.isIWSApplied
                            ? goalsData?.recommendedWealthSplit
                            : calculatedAmountval.initialInvestment
                    }
                    // will be referring to originalGoalData present in recommendation
                    originalInvestmentAmount={
                        goalDataRecomendation.oneTimeTopUp && goalDataRecomendation.originalData?.request
                            ? goalsData?.isIWSApplied
                                ? goalDataRecomendation.originalData?.request?.recommendedWealthSplit
                                : calculateInitialAmount(goalDataRecomendation.originalData?.request?.accounts)
                                      .initialInvestment
                            : null
                    }
                    planReRunOptions={[
                        {
                            optionLabel: t('PDF_LABEL_RECOMMENDATIONS_LIST_ADD_A_LUMP_SUM'),
                            key: 'oneTimeTopUp',
                            optionValue: analysisReport['oneTimeTopUp']
                        },
                        {
                            optionLabel: t('PDF_LABEL_RECOMMENDATIONS_LIST_INCREASE_ANNUAL_CONTRIBUTIONS'),
                            key: 'topUpAccumulation',
                            optionValue: analysisReport['yearlyTopUpAccumulation']
                        },
                        {
                            optionLabel: t('PDF_LABEL_RECOMMENDATIONS_LIST_DECREASE_ANNUAL_INCOME'),
                            key: 'topUpDecumulation',
                            optionValue: isRetirement ? analysisReport['yearlyTopUpDecumulation'] : 0
                        },
                        {
                            optionLabel: isRetirement ? t('DELAY_RETIREMENT') : t('DELAY_GOAL'),
                            key: 'recommendedTenure',
                            optionValue: extractYearForProbability(analysisReport['recommendedTenure'])
                        }
                    ]}
                    yearsToGoal={getFormattedDate(
                        isRetirement
                            ? dayjs(goalsData?.planStartRetirement, 'DD-MM-YYYY')
                            : dayjs(goalsData?.targetDate, 'DD-MM-YYYY')
                    )}
                    originalYearsToGoals={
                        goalDataRecomendation?.recomendedTenure && goalDataRecomendation?.originalData?.request
                            ? getFormattedDate(
                                  isRetirement
                                      ? dayjs(
                                            goalDataRecomendation?.originalData?.request?.planStartRetirement,
                                            'DD-MM-YYYY'
                                        )
                                      : dayjs(goalDataRecomendation?.originalData?.request?.targetDate, 'DD-MM-YYYY')
                              )
                            : null
                    }
                    yearsInRetirement={
                        isRetirement
                            ? getFormattedDate(
                                  dayjs(goalsData?.targetDate, 'DD-MM-YYYY'), // ref: AEX-2198 CommentId=1879302 point #2
                                  'MMMM, YYYY'
                              )
                            : ''
                    }
                    originalYearsToRetirement={
                        goalDataRecomendation?.recomendedTenure && goalDataRecomendation?.originalData?.request
                            ? isRetirement
                                ? getFormattedDate(
                                      dayjs(
                                          goalDataRecomendation?.originalData?.request?.targetDate,
                                          'DD-MM-YYYY'
                                      ).subtract(1, 'year'),
                                      'MMMM, YYYY'
                                  )
                                : ''
                            : null
                    }
                    isRetirement={isRetirement}
                    isRecomendation={isRecomendation}
                    isPDF={isPDF}
                    thresholdValue={goalPriorityProbability}
                />
            </ProbSuccessDiv>
        </>
    );
    const GoalContentUnrealistic = () => (
        <>
            <GoalHeaderContent />
            <PlanSummaryProgressBar
                highlightedValues={
                    isRetirement
                        ? [
                              {
                                  amount: goalAmount,
                                  label: t('TARGET_ANNUAL_INCOME'),
                                  helperText: t('TARGET_ANNUAL_INCOME_HELPER'),
                                  originalAmount:
                                      isRecommendationApplied &&
                                      goalAmount !==
                                          goalDataRecomendation?.originalData?.request?.targetedRetirementIncome
                                          ? goalDataRecomendation?.originalData?.request?.targetedRetirementIncome
                                          : 0
                              },
                              {
                                  amount: probableGoalAmount,
                                  label: t('PROJECTED_ANNUAL_INCOME'),
                                  helperText: t('PROJECTED_ANNUAL_INCOME_HELPER'),
                                  helperTextProps: {
                                      thresholdValue: goalPriorityProbability
                                  },
                                  originalAmount: isRecommendationApplied
                                      ? originalGoalAmtRetirement(goalDataRecomendation?.originalData)
                                      : 0
                              },
                              ...(bequestAmount
                                  ? [
                                        {
                                            amount: bequestAmount,
                                            label: t('GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT'),
                                            helperText: t('BEQUEST_AMOUNT_HELPER_TEXT')
                                        }
                                    ]
                                  : [])
                          ]
                        : [
                              {
                                  amount: goalAmount,
                                  label: t('TARGET_GOAL'),
                                  helperText: t('TARGET_GOAL_HELPER') + convertDateFormat(goalsData?.targetDate)
                              },

                              {
                                  amount: probableGoalAmount,
                                  label: t('PROJECTED_GOAL'),
                                  helperText: t('PROJECTED_GOAL_HELPER'),
                                  helperTextProps: {
                                      thresholdValue: goalPriorityProbability
                                  },
                                  originalAmount: isRecommendationApplied
                                      ? goalDataRecomendation?.originalData?.responseGoalCalculator?.response?.body
                                            ?.goalAmt
                                      : 0
                              }
                          ]
                }
                onClickEditGoals={() => setShowGoalDrawer(true)}
                targetProgress={goalAmount < probableGoalAmount ? barPrecentage : 100}
                values={planSummaryProgressBarValues}
                isPDF={isPDF}
                showReset={isRecommendationApplied}
                onClickReset={handleConfirmReset}
                isGoalRealistic={isGoalRealistic}
            />
            <ProbSuccessDiv>
                <ProbabilityOfSuccess
                    dangerValue={goalUnrealisticValue * 100}
                    goalType={goalsData['goalPriority']}
                    target={goalPriorityProbability}
                    thresholdValue={goalPriorityProbability}
                    value={currentGoalProbability}
                    isPDF={isPDF}
                    isGoalRealistic={isGoalRealistic}
                    onGoalClick={() => setShowGoalDrawer(true)}
                    onAccountsClick={() => dispatch(setOpenDrawer('accounts'))}
                    onIncomeSourcesClick={() => dispatch(setOpenDrawer('incomeSources'))}
                />
                <PlanInfoAndReRunSection
                    anualContributionDict={anualContributionDict}
                    originalAnnualContributionsAmount={originalAnnualContributionDict?.infusion ?? null}
                    handleReRunButtonClicked={(selectedRecomendation) => {
                        applyRecommendation(selectedRecomendation);
                    }}
                    initialInvestmentAmount={
                        goalsData?.isIWSApplied
                            ? goalsData?.recommendedWealthSplit
                            : calculatedAmountval.initialInvestment
                    }
                    // will be referring to originalGoalData present in recommendation
                    originalInvestmentAmount={
                        goalDataRecomendation.oneTimeTopUp && goalDataRecomendation.originalData?.request
                            ? goalsData?.isIWSApplied
                                ? goalDataRecomendation.originalData?.request?.recommendedWealthSplit
                                : calculateInitialAmount(goalDataRecomendation.originalData?.request?.accounts)
                                      .initialInvestment
                            : null
                    }
                    planReRunOptions={[]} // Empty array since goal is unrealistic, recommendation cannot be applied
                    yearsToGoal={getFormattedDate(
                        isRetirement
                            ? dayjs(goalsData?.planStartRetirement, 'DD-MM-YYYY')
                            : dayjs(goalsData?.targetDate, 'DD-MM-YYYY')
                    )}
                    originalYearsToGoals={
                        goalDataRecomendation?.recomendedTenure && goalDataRecomendation?.originalData?.request
                            ? getFormattedDate(
                                  isRetirement
                                      ? dayjs(
                                            goalDataRecomendation?.originalData?.request?.planStartRetirement,
                                            'DD-MM-YYYY'
                                        )
                                      : dayjs(goalDataRecomendation?.originalData?.request?.targetDate, 'DD-MM-YYYY')
                              )
                            : null
                    }
                    yearsInRetirement={
                        isRetirement
                            ? getFormattedDate(
                                  dayjs(goalsData?.targetDate, 'DD-MM-YYYY'), // ref: AEX-2198 CommentId=1879302 point #2
                                  'MMMM, YYYY'
                              )
                            : ''
                    }
                    originalYearsToRetirement={
                        goalDataRecomendation?.recomendedTenure && goalDataRecomendation?.originalData?.request
                            ? isRetirement
                                ? getFormattedDate(
                                      dayjs(
                                          goalDataRecomendation?.originalData?.request?.targetDate,
                                          'DD-MM-YYYY'
                                      ).subtract(1, 'year'),
                                      'MMMM, YYYY'
                                  )
                                : ''
                            : null
                    }
                    isRetirement={isRetirement}
                    isRecomendation={true} // since goal is unrealistic, recommendation cannot be applied
                    isPDF={isPDF}
                    thresholdValue={goalPriorityProbability}
                />
            </ProbSuccessDiv>
            <ConfirmationCentered
                onCancellation={handleCancelDelete}
                onCloseIconClick={handleCancelDelete}
                onConfirmation={() => handleConfirmDelete()}
                open={deleteModalState}
                content={`${t('DELETE_GOAL_CONTENT')}`}
                title={`${t('DELETE_GOAL_TITLE')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                width="100%"
            />
        </>
    );

    return (
        <>
            <>
                {isGoalRealistic ? (
                    isPDF ? (
                        <Grid height={'1000px'} position={'relative'} marginBottom={'40px'}>
                            <PDFHeader pageName={t('GOAL_DETAILS_TITLE')} />
                            <GoalContent />
                            <PDFFooter
                                currentPage={
                                    adviceGoals?.response.reduce((acc, goal, index) => {
                                        if (index >= adviceGoals?.response?.findIndex((g) => g.goalId === goalId)) {
                                            return acc;
                                        }
                                        return goal?.response?.body?.analysisReport?.isGoalRealistic
                                            ? acc + 4
                                            : acc + 1;
                                    }, 0) + 1
                                }
                                totalPages={totalPagesForPDF}
                            />
                        </Grid>
                    ) : (
                        <UniversalCard
                            cardSx={{
                                marginBottom: '24px'
                            }}
                            header={t('GOAL_DETAILS_HEADER')}
                            onClick={() => {}}
                            onClickViewAll={() => {}}
                            actionMenuOptions={[
                                {
                                    label: t('DELETE_TEXT'),
                                    onClick: () => setDeleteModalState(true),
                                    icon: 'delete'
                                }
                            ]}
                        >
                            <GoalContent />
                            <ConfirmationCentered
                                onCancellation={handleCancelDelete}
                                onCloseIconClick={handleCancelDelete}
                                onConfirmation={() => handleConfirmDelete()}
                                open={deleteModalState}
                                content={`${t('DELETE_GOAL_CONTENT')}`}
                                title={`${t('DELETE_GOAL_TITLE')}`}
                                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                                width="100%"
                            />
                        </UniversalCard>
                    )
                ) : isPDF ? (
                    <Grid height={'1000px'} position={'relative'} marginBottom={'40px'}>
                        <PDFHeader pageName={t('GOAL_DETAILS_TITLE')} />
                        <GoalContentUnrealistic />
                        <PDFFooter
                            currentPage={
                                adviceGoals?.response.reduce((acc, goal, index) => {
                                    if (index >= adviceGoals?.response?.findIndex((g) => g.goalId === goalId)) {
                                        return acc;
                                    }
                                    return goal?.response?.body?.analysisReport?.isGoalRealistic ? acc + 4 : acc + 1;
                                }, 0) + 1
                            }
                            totalPages={totalPagesForPDF}
                        />
                    </Grid>
                ) : (
                    <UniversalCard
                        cardSx={{
                            marginBottom: '24px',
                            paddingBottom: '26px'
                        }}
                        header={t('GOAL_DETAILS_HEADER')}
                        onClick={() => {}}
                        onClickViewAll={() => {}}
                        actionMenuOptions={[
                            {
                                label: t('DELETE_TEXT'),
                                onClick: () => setDeleteModalState(true),
                                icon: 'delete'
                            }
                        ]}
                    >
                        <GoalContentUnrealistic />
                    </UniversalCard>
                )}

                {isGoalRealistic ? (
                    isPDF ? (
                        <>
                            <PDFProjectedWealthPathChart
                                currentPages={
                                    adviceGoals?.response.reduce((acc, goal, index) => {
                                        if (index >= adviceGoals?.response?.findIndex((g) => g.goalId === goalId)) {
                                            return acc;
                                        }
                                        return goal?.response?.body?.analysisReport?.isGoalRealistic
                                            ? acc + 4
                                            : acc + 1;
                                    }, 0) + 2
                                }
                                totalPages={totalPagesForPDF}
                                data={chartData}
                                targetPercentile={wealthPath?.defaultPercentile}
                                lowPercentile={wealthPath?.pessimisticPercentile}
                                highPercentile={wealthPath?.optimisticPercentile}
                                // bankruptcyYear={bankruptcyYear}
                                goalPriority={goalPriority}
                                priority={goalPriority}
                                type={type}
                                goalName={goalName}
                                goalType={goalType}
                                retirementYear={isRetirement && Number(goalsData?.planStartRetirement?.split('-')[2])}
                                plotLine={
                                    isRetirement && {
                                        year: Number(goalsData?.planStartRetirement?.split('-')[2]),
                                        type: 'retirement'
                                    }
                                }
                                disclaimer={cmsUtil.getContentAsString('plan_detail.wealth_projection.disclaimer')}
                                targetGoalAmount={goalAmount}
                                projectedGoalAmount={probableGoalAmount}
                                goalProbabilityPercentile={currentGoalProbability}
                                goalTargetDate={convertDateFormat(
                                    isRetirement ? goePayload.endDate : goalsData.targetDate
                                )}
                                goalPriorityProbability={goalPriorityProbability}
                                isRetirement={isRetirement}
                                isRecomendation={isRecomendation}
                            />
                            <PDFPortfolioComposition
                                recommendedPortfolioId={analysisReport['recommendedPortfolioId']}
                                allMarketPortfolios={marketPortfoliosForComposition}
                                priority={goalPriority}
                                type={type}
                                goalName={goalName}
                                goalType={goalType}
                                totalPages={totalPagesForPDF}
                                currentPage={
                                    adviceGoals?.response.reduce((acc, goal, index) => {
                                        if (index >= adviceGoals?.response?.findIndex((g) => g.goalId === goalId)) {
                                            return acc;
                                        }
                                        return goal?.response?.body?.analysisReport?.isGoalRealistic
                                            ? acc + 4
                                            : acc + 1;
                                    }, 0) + 3
                                }
                                disclaimer={
                                    cms_projectedAllocationPart1_asString + '<br/>' + cms_RiskToleranceDescription
                                }
                            />
                            <PDFAllocationsOverTimeChart
                                priority={goalPriority}
                                type={type}
                                goalName={goalName}
                                goalType={goalType}
                                portfolioPath={pathReport?.portfolioPath}
                                retirementYear={isRetirement && Number(goalsData?.planStartRetirement?.split('-')[2])}
                                tenure={selectedGoal?.goalCategory}
                                currentPage={
                                    adviceGoals?.response.reduce((acc, goal, index) => {
                                        if (index >= adviceGoals?.response?.findIndex((g) => g.goalId === goalId)) {
                                            return acc;
                                        }
                                        return goal?.response?.body?.analysisReport?.isGoalRealistic
                                            ? acc + 4
                                            : acc + 1;
                                    }, 0) + 4
                                }
                                totalPages={totalPagesForPDF}
                            />
                        </>
                    ) : (
                        <>
                            <UniversalCard>
                                <Tabs
                                    value={tabIndex}
                                    onChange={handleTabChange}
                                    variant={TabVariant.COMPACT_TAB}
                                    tabPanels={[
                                        {
                                            enabled: true,
                                            hidden: false,
                                            title: t('LABEL_PORTFOLIO_COMPOSITION'),
                                            children: (
                                                <PortfolioCompositionContainer
                                                    recommendedPortfolioId={analysisReport['recommendedPortfolioId']}
                                                    allMarketPortfolios={marketPortfoliosForComposition}
                                                    tenure={selectedGoal?.goalCategory}
                                                />
                                            )
                                        },
                                        {
                                            enabled: true,
                                            hidden: false,
                                            title: t('LABEL_ALLOCATION_TAB'),
                                            children: (
                                                <Grid>
                                                    <AllocationsOverTimeTab
                                                        portfolioPath={pathReport?.portfolioPath}
                                                        scenarioType={scenarioType}
                                                        tenure={selectedGoal?.goalCategory}
                                                    />
                                                    <ProjectedWealthPathChart
                                                        data={chartData}
                                                        targetPercentile={wealthPath?.defaultPercentile}
                                                        lowPercentile={wealthPath?.pessimisticPercentile}
                                                        highPercentile={wealthPath?.optimisticPercentile}
                                                        // bankruptcyYear={bankruptcyYear}
                                                        goalPriority={goalPriority}
                                                        goalPriorityProbability={goalPriorityProbability}
                                                        plotLine={
                                                            isRetirement && {
                                                                year: Number(
                                                                    goalsData?.planStartRetirement?.split('-')[2]
                                                                ),
                                                                type: 'retirement'
                                                            }
                                                        }
                                                        targetGoalAmount={goalAmount}
                                                        projectedGoalAmount={probableGoalAmount}
                                                        goalProbabilityPercentile={currentGoalProbability}
                                                        goalTargetDate={convertDateFormat(
                                                            isRetirement ? goePayload.endDate : goalsData.targetDate
                                                        )}
                                                        isRetirement={isRetirement}
                                                        isRecomendation={isRecomendation}
                                                    />
                                                </Grid>
                                            )
                                        }
                                    ]}
                                />
                            </UniversalCard>
                        </>
                    )
                ) : null}
                {isGoalRealistic &&
                    !isPDF &&
                    (tabIndex == '1' ? (
                        <></>
                    ) : (
                        <>
                            <StyledDisclaimerContent label={cms_portfolioCompositionDescription} />
                            <StyledDisclaimerContent
                                label={
                                    <>
                                        {cms_projectedAllocationPart1_asElement}
                                        <br />
                                        {cms_RiskToleranceDescription}
                                    </>
                                }
                            />
                        </>
                    ))}
            </>
            {isRetirement ? (
                <RetirementGoalsFF
                    selectedGoal={{ title: goalType, type: goalType }}
                    handleClose={() => setShowGoalDrawer(false)}
                    open={showGoalDrawer}
                    yearsInRetirement={parseInt(yearsInRetirement)}
                    goalData={{ ...formField?.data, ...goalsData, goalId, title }}
                    callRunpipe={callRunpipe}
                    goalsWithNoFundingSource={goalsWithNoFundingSource}
                />
            ) : (
                <WealthGoals
                    selectedGoal={{ title: goalType, type: goalType }}
                    handleClose={() => setShowGoalDrawer(false)}
                    open={showGoalDrawer}
                    goalData={{ ...goalsData, goalId, title }}
                    callRunpipe={callRunpipe}
                    goalsWithNoFundingSource={goalsWithNoFundingSource}
                />
            )}
        </>
    );
};

export default GoalSummary;
